// AutoLock — fires lock() after `timeoutMs` of inactivity.
//
// UI calls `bump()` on user activity (mousemove, keydown, click, touchstart).
// `start()` arms the timer; `stop()` clears it. Visibility hidden optionally
// locks immediately if `lockOnHide` is true.

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export class AutoLock {
  constructor(vault, opts = {}) {
    this._vault = vault;
    this._timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this._lockOnHide = opts.lockOnHide ?? false;
    this._onLock = opts.onLock ?? null;
    this._timer = null;
    this._setTimeout = opts.setTimeout ?? globalThis.setTimeout.bind(globalThis);
    this._clearTimeout = opts.clearTimeout ?? globalThis.clearTimeout.bind(globalThis);
    this._target = opts.target ?? null;   // element/document for activity events
    this._pauseDepth = 0;                 // nesting count for pause()/resume()
    this._handleActivity = () => this.bump();
    this._handleVisibility = () => {
      if (this._lockOnHide && globalThis.document?.hidden) this._fire();
    };
  }

  setTimeoutMs(ms) {
    this._timeoutMs = ms;
    if (this._timer) this.bump();
  }

  start() {
    this.bump();
    const t = this._target;
    if (t) {
      for (const evt of ['mousemove', 'keydown', 'click', 'touchstart', 'scroll']) {
        t.addEventListener(evt, this._handleActivity, { passive: true });
      }
      globalThis.document?.addEventListener('visibilitychange', this._handleVisibility);
    }
  }

  stop() {
    if (this._timer) this._clearTimeout(this._timer);
    this._timer = null;
    const t = this._target;
    if (t) {
      for (const evt of ['mousemove', 'keydown', 'click', 'touchstart', 'scroll']) {
        t.removeEventListener(evt, this._handleActivity);
      }
      globalThis.document?.removeEventListener('visibilitychange', this._handleVisibility);
    }
  }

  bump() {
    if (this._timer) this._clearTimeout(this._timer);
    if (this._pauseDepth > 0) { this._timer = null; return; }
    this._timer = this._setTimeout(() => this._fire(), this._timeoutMs);
  }

  /**
   * Suspend the idle timer while a modal holds unsaved work. Nesting-safe:
   * stacking a second sheet over an edit form used to have the inner sheet's
   * dismiss re-arm auto-lock while the outer form was still open, so walking
   * away mid-edit discarded the entry the pause existed to protect.
   */
  pause() {
    this._pauseDepth++;
    if (this._timer) { this._clearTimeout(this._timer); this._timer = null; }
  }

  /** Balance a pause(). Only the outermost resume re-arms the timer. */
  resume() {
    if (this._pauseDepth === 0) return;
    this._pauseDepth--;
    if (this._pauseDepth === 0) this.bump();
  }

  _fire() {
    // Drop the handle only after cancelling: a lockOnHide fire used to abandon
    // the pending bump() timeout, leaving a timer no stop()/bump() could clear.
    if (this._timer) this._clearTimeout(this._timer);
    this._timer = null;
    if (this._vault?.isUnlocked?.()) {
      this._vault.lock();
      this._onLock?.();
    }
  }
}
